import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import type { DashboardSettings } from '../services/dashboardSettingsService';

interface DashboardSettingsCardProps {
  settings: DashboardSettings;
  onEdit: () => void;
}

const DAYS_OF_WEEK = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export function DashboardSettingsCard({
  settings,
  onEdit,
}: DashboardSettingsCardProps) {
  return (
    <div className="bg-white border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Dashboard Alert Settings
        </h3>
        <Button size="sm" onClick={onEdit}>
          Edit Settings
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* Overdue Ordered */}
        <div className="border-l-4 border-red-500 pl-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-900">
              Overdue Ordered Frames
            </h4>
            <Badge variant={settings.enable_overdue_ordered ? 'success' : 'neutral'}>
              {settings.enable_overdue_ordered ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
          <div className="text-sm text-gray-600 space-y-1">
            <p>
              Yellow: {settings.overdue_ordered_yellow_days} days
            </p>
            <p>
              Red: {settings.overdue_ordered_red_days} days
            </p>
          </div>
        </div>

        {/* Stale Pending */}
        <div className="border-l-4 border-yellow-500 pl-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-900">
              Stale Pending Frames
            </h4>
            <Badge variant={settings.enable_stale_pending ? 'success' : 'neutral'}>
              {settings.enable_stale_pending ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
          <div className="text-sm text-gray-600 space-y-1">
            <p>
              Yellow: {settings.stale_pending_yellow_days} days
            </p>
            <p>
              Red: {settings.stale_pending_red_days} days
            </p>
          </div>
        </div>

        {/* Receiving Backlog */}
        <div className="border-l-4 border-blue-500 pl-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-900">
              Receiving Backlog
            </h4>
            <Badge variant={settings.enable_receiving_backlog ? 'success' : 'neutral'}>
              {settings.enable_receiving_backlog ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
          <div className="text-sm text-gray-600">
            <p>
              Threshold: {settings.receiving_backlog_days} days
            </p>
          </div>
        </div>

        {/* Usage Alert */}
        <div className="border-l-4 border-green-500 pl-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-900">
              Usage Alert
            </h4>
            <Badge variant={settings.enable_usage_alert ? 'success' : 'neutral'}>
              {settings.enable_usage_alert ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
          <div className="text-sm text-gray-600 space-y-1">
            <p>
              Yellow: {settings.usage_yellow_pct}%
            </p>
            <p>
              Red: {settings.usage_red_pct}%
            </p>
          </div>
        </div>

        {/* Ordering Day */}
        <div className="border-l-4 border-purple-500 pl-4 col-span-2">
          <h4 className="text-sm font-medium text-gray-900 mb-2">
            Preferred Ordering Day
          </h4>
          <div className="text-sm text-gray-600">
            <p>
              {DAYS_OF_WEEK[settings.ordering_day_of_week] || 'Not set'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
